/**
 * zodResolver.ts
 *
 * Local polyfill for @hookform/resolvers/zod.
 * Converts ZodError into the FieldErrors shape that react-hook-form expects.
 *
 * This avoids a separate npm install while keeping all validation logic intact.
 */
import type { ZodType, ZodTypeDef, ZodError } from 'zod';
import type { Resolver, FieldValues } from 'react-hook-form';

/** Recursively sets a value at a dotted path on an object */
function setNestedError(
  errors: Record<string, unknown>,
  path: Array<string | number>,
  value: unknown,
): void {
  let cursor: Record<string, unknown> = errors;
  for (let i = 0; i < path.length - 1; i++) {
    const key = String(path[i]);
    if (!cursor[key] || typeof cursor[key] !== 'object') {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  const last = String(path[path.length - 1]);
  if (!cursor[last]) {
    cursor[last] = value;
  }
}

function toFieldErrors(zodError: ZodError): Record<string, unknown> {
  const errors: Record<string, unknown> = {};
  for (const issue of zodError.issues) {
    if (issue.path.length === 0) continue;
    setNestedError(errors, issue.path, {
      type: issue.code,
      message: issue.message,
    });
  }
  return errors;
}

/**
 * `ZodSchema<T>` is `ZodType<T, ZodTypeDef, T>` — it pins the schema's INPUT
 * type to its output. That holds for a plain object schema, but `z.preprocess`
 * (see `@/lib/legacyZod`) deliberately takes `unknown` in and yields a narrowed
 * value out, and with one type parameter inference locked onto the input side:
 * `useForm<FormValues>` asked for a `Resolver` over the parsed shape and got
 * one over the raw shape instead. Naming both sides lets `TOutput` come from
 * what the schema produces, which is what the form actually holds.
 */
export function zodResolver<TOutput extends FieldValues, TInput = TOutput>(
  schema: ZodType<TOutput, ZodTypeDef, TInput>,
): Resolver<TOutput> {
  return async (values) => {
    const result = schema.safeParse(values);
    if (result.success) {
      return { values: result.data, errors: {} };
    }
    return {
      values: {} as TOutput,
      errors: toFieldErrors(result.error) as ReturnType<Resolver<TOutput>> extends Promise<infer R> ? R extends { errors: infer E } ? E : never : never,
    };
  };
}
